import React from "react";
import {
  FileText,
  Settings,
  Folder,
  CheckCircle,
  MapPin,
  Calendar,
  Shield,
  Upload,
  Tag,
  Eye,
  Layers,
  BadgeDollarSign,
  BadgeAlert,
  Blinds,
  LucideTextCursor,
  LayoutListIcon,
  Library,
  Grid2X2Icon,
  Group,
  AlignVerticalSpaceAround,
} from "lucide-react";
import type { Field, DocumentItem } from "../types/rfq";

export const CERTIFICATIONS = [
  "ISO9001",
  "IATF16949",
  "ISO14001",
  "ISO45001",
  "ISO27001",
];

export const DOCUMENT_TYPE_OPTIONS = [
  { value: "plan", label: "doc_plan" },
  { value: "cdc", label: "doc_cdc" },
  { value: "spec", label: "doc_spec" },
  { value: "3d", label: "doc_3d" },
  { value: "photo", label: "doc_photo" },
  { value: "autre", label: "doc_other" },
];

export const NON_NEGATIVE_FIELDS = ["quantity", "annual_volume", "budget"];
export const COMPACT_CHOICE_FIELDS = ["request_type", "priority", "criteria"];

export const initialState = {
  documents: [] as DocumentItem[],
  cities: [] as string[],
};

export const stepDescriptions: Record<number, string> = {
  1: "describe_need",
  2: "specifications_subtitle",
  3: "documents_subtitle",
  4: "publication_subtitle",
};

export const steps = [
  { id: 1, title: "information", icon: <FileText size={16} /> },
  { id: 2, title: "specifications", icon: <Settings size={16} /> },
  { id: 3, title: "documents", icon: <Folder size={16} /> },
  { id: 4, title: "publication", icon: <CheckCircle size={16} /> },
];

const iconStyle = {
  size: 16,
  color: "#f97316",
};

export const getFieldIcon = (id: string) => {
  switch (id) {
    case "title":
      return <LucideTextCursor {...iconStyle} />;
    case "category":
      return <Folder {...iconStyle} />;
    case "segment":
      return <Grid2X2Icon {...iconStyle} />;
    case "programme":
      return <Library {...iconStyle} />;
    case "reference_piece":
      return <Group {...iconStyle} />;
    case "sourcing_type":
      return <AlignVerticalSpaceAround {...iconStyle} />;
    case "description":
      return <FileText {...iconStyle} />;
    case "request_type":
      return <Settings {...iconStyle} />;
    case "priority":
      return <LayoutListIcon {...iconStyle} />;
    case "confidentiality":
      return <Shield {...iconStyle} />;
    case "quantity":
      return <Blinds {...iconStyle} />;
    case "annual_volume":
      return <Layers {...iconStyle} />;
    case "start_date":
    case "deadline":
    case "delivery_date":
    case "response_deadline":
      return <Calendar {...iconStyle} />;
    case "certifications":
      return <Shield {...iconStyle} />;
    case "country":
    case "cities":
      return <MapPin {...iconStyle} />;
    case "logistics":
      return <Settings {...iconStyle} />;
    case "incoterm":
      return <Tag {...iconStyle} />;
    case "budget_type":
      return <BadgeAlert {...iconStyle} />;
    case "budget":
      return <BadgeDollarSign {...iconStyle} />;
    case "documents":
      return <Upload {...iconStyle} />;
    case "visibility":
      return <Eye {...iconStyle} />;
    case "criteria":
      return <CheckCircle {...iconStyle} />;
    case "message":
      return <FileText {...iconStyle} />;
    default:
      return <Settings {...iconStyle} />;
  }
};

export const step1: Field[] = [
  {
    id: "title",
    label: "title_request",
    type: "text",
    col: 6,
    placeholder: "title_request_placeholder",
  },
  {
    id: "category",
    label: "category",
    type: "SelectGroup",
    col: 6,
    searchable: false,
    options: [
      { value: "mecanique", label: "category_mechanical_parts" },
      { value: "plastique", label: "category_plastic_parts" },
      { value: "electronique", label: "category_electronics" },
      { value: "services", label: "category_industrial_services" },
    ],
  },
  {
    id: "segment",
    label: "segment",
    type: "text",
    col: 6,
    placeholder: "segment_placeholder",
  },
  {
    id: "programme",
    label: "program",
    type: "text",
    col: 6,
    placeholder: "program_placeholder",
  },
  {
    id: "reference_piece",
    label: "part_reference",
    type: "text",
    col: 6,
    placeholder: "part_reference_placeholder",
  },
  {
    id: "sourcing_type",
    label: "sourcing_type",
    type: "radio",
    col: 6,
    options: ["local", "international", "mixed"],
  },
  {
    id: "request_type",
    label: "request_type",
    type: "checkbox",
    col: 12,
    options: ["prototype", "pilot_series", "mass_production", "service"],
  },
  {
    id: "priority",
    label: "priority",
    type: "radio",
    col: 12,
    options: ["low", "normal", "urgent"],
  },
  {
    id: "description",
    label: "description",
    type: "textarea",
    col: 12,
    placeholder: "description_placeholder",
  },
];

export const step2: Field[] = [
  { id: "start_date", label: "start_date", type: "date", col: 4 },
  { id: "deadline", label: "offer_deadline", type: "date", col: 4 },
  { id: "delivery_date", label: "first_delivery", type: "date", col: 4 },
  {
    id: "country",
    label: "supplier_country",
    type: "SelectGroup",
    col: 6,
    searchable: true,
    options: [],
  },
  { id: "cities", label: "cities", type: "tags", col: 6 },
  {
    id: "quantity",
    label: "requested_quantity",
    type: "number",
    col: 4,
    placeholder: "requested_quantity_placeholder",
  },
  {
    id: "annual_volume",
    label: "annual_volume",
    type: "number",
    col: 4,
    placeholder: "annual_volume_placeholder",
  },
  {
    id: "budget",
    label: "estimated_budget",
    type: "number",
    col: 4,
    placeholder: "estimated_budget_placeholder",
  },
  {
    id: "incoterm",
    label: "incoterm",
    type: "text",
    col: 6,
    placeholder: "incoterm_placeholder",
  },
  {
    id: "certifications",
    label: "required_certifications",
    type: "certifications",
    col: 4,
  },
  {
    id: "logistics",
    label: "logistics_requirements",
    type: "checkbox",
    col: 8,
    options: [
      "on_site_delivery",
      "specific_packaging",
      "labeling",
      "traceability",
    ],
  },
  {
    id: "budget_type",
    label: "budget_type",
    type: "radio",
    col: 12,
    options: ["to_be_discussed", "indicative", "fixed"],
  },
];

export const step3: Field[] = [
  { id: "documents", label: "technical_documents", type: "file", col: 12 },
];

export const step4: Field[] = [
  {
    id: "response_deadline",
    label: "response_deadline",
    type: "date",
    col: 6,
  },
  {
    id: "visibility",
    label: "request_visibility",
    type: "radio",
    col: 6,
    options: [
      "all_qualified_suppliers",
      "preselected_suppliers",
      "private_invitation",
    ],
  },
  {
    id: "criteria",
    label: "evaluation_criteria",
    type: "checkbox",
    col: 12,
    options: [
      "price",
      "quality",
      "delay",
      "capacity",
      "location",
      "certification",
      "experience",
    ],
  },
  {
    id: "message",
    label: "final_message",
    type: "textarea",
    col: 12,
    placeholder: "final_message_placeholder",
  },
];

export const wizardConfig: Record<number, Field[]> = {
  1: step1,
  2: step2,
  3: step3,
  4: step4,
};